import { DATE_FORMAT_PATTERN_DD_MM_YYYY, WEEKYEAR_FORMAT_PATTERN } from '../../shared/models/filter-formats';

export interface ValidationConfig {
	type: 'string' | 'number' | 'date';
	row: number;
	column: number;
	mandatory: boolean;
	errorCodes: string[];
	errorParamX?: 'size' | 'value';
	errorParamY?: string;
	skipOnError?: boolean;
	minLength?: number;
	maxLength?: number;
	trimOnExceed?: boolean;
	min?: number;
	max?: number;
	decimalCount?: number;
	defaultValue?: string;
	isDropDown?: boolean;
	values?: (string | number)[];
	dateFormat?: string;
}

export interface ErrorObj {
	row: number;
	errorCode: string;
	param1?: string;
	param2?: string;
}

export interface CellData {
	value: string;
	error?: ErrorObj;
	isValidType: boolean;
}

export const globalValidationConfig: Record<string, ValidationConfig> = {
	itemNumberStart: {
		type: 'string',
		row: 7,
		column: 0,
		mandatory: true,
		errorCodes: ['fastlane.template.format.error'],
		values: ['001', '1']
	},
	templateName: {
		type: 'string',
		row: 0,
		column: 0,
		mandatory: true,
		errorCodes: ['fastlane.template.format.error'],
		values: ['FAST LANE Request - ArcelorMittal', 'Requête FAST LANE - ArcelorMittal']
	},
};

export const headerValidationConfig: Record<string, ValidationConfig> = {
	requestName: {
		type: 'string',
		row: 1,
		column: 2,
		mandatory: true,
		maxLength: 120,
		trimOnExceed: true,
		errorCodes: ['fastlane.template.requestname.validation'],
		errorParamX: 'size'
	},
	description: {
		type: 'string',
		row: 1,
		column: 5,
		mandatory: true,
		maxLength: 250,
		trimOnExceed: true,
		defaultValue: '',
		errorCodes: ['fastlane.template.description.validation'],
		errorParamX: 'size'
	},
	currency: {
		type: 'string',
		row: 1,
		column: 17,
		mandatory: false,
		defaultValue: 'EUR',
		isDropDown: true,
		errorCodes: ['fastlane.template.currency.validation'],
		errorParamX: 'value'
	},
	shipToCustom: {
		type: 'string',
		row: 3,
		column: 2,
		mandatory: false,
		maxLength: 150,
		trimOnExceed: true,
		defaultValue: 'LATER',
		errorCodes: ['fastlane.template.deliveryaddress.validation'],
		errorParamX: 'size'
	}
};

export const itemValidationConfig: Record<string, ValidationConfig> = {
	itemNumber: {
		type: 'string',
		row: 7,
		column: 0,
		mandatory: true,
		errorCodes: ['fastlane.template.request', 'fastlane.template.requestname.validation'],
	},
	name: {
		type: 'string',
		row: 7,
		column: 1,
		mandatory: true,
		maxLength: 80,
		trimOnExceed: true,
		defaultValue: '',
		errorCodes: ['fastlane.template.itemname.validation'],
		errorParamX: 'size'
	},
	steelgrade: {
		type: 'string',
		row: 7,
		column: 2,
		mandatory: true,
		maxLength: 255,
		trimOnExceed: true,
		defaultValue: '',
		errorCodes: ['fastlane.template.steelgrade.validation'],
		errorParamX: 'size'
	},
	form: {
		type: 'string',
		row: 7,
		column: 3,
		mandatory: true,
		isDropDown: true,
		defaultValue: 'Coil',
		errorCodes: ['fastlane.template.form.validation'],
		errorParamX: 'value'
	},
	thickness: {
		type: 'number',
		row: 7,
		column: 4,
		mandatory: true,
		min: 0.1,
		max: 999,
		decimalCount: 2,
		trimOnExceed: true,
		errorCodes: ['fastlane.template.thickness.validation'],
		errorParamX: 'value'
	},
	width: {
		type: 'number',
		row: 7,
		column: 5,
		mandatory: true,
		min: 1,
		max: 999_999_999,
		decimalCount: 0,
		trimOnExceed: true,
		errorCodes: ['fastlane.template.width.validation'],
		errorParamX: 'value'
	},
	length: {
		type: 'number',
		row: 7,
		column: 6,
		mandatory: true,
		min: 1,
		max: 999_999_999,
		decimalCount: 0,
		trimOnExceed: true,
		errorCodes: ['fastlane.template.length.validation'],
		errorParamX: 'value'
	},
	blankWeight: {
		type: 'number',
		row: 7,
		column: 7,
		mandatory: true,
		min: 1,
		max: 99_999_999,
		decimalCount: 2,
		trimOnExceed: true,
		errorCodes: ['fastlane.template.blankweight.validation'],
		errorParamX: 'value'
	},
	quantityUnit: {
		type: 'string',
		row: 7,
		column: 9,
		mandatory: true,
		isDropDown: true,
		defaultValue: 'tons',
		errorCodes: ['fastlane.template.unitbasic.validation'],
		errorParamX: 'value'
	},
	quantity: {
		type: 'number',
		row: 7,
		column: 8,
		mandatory: true,
		min: 1,
		max: 99_999_999,
		decimalCount: 2,
		trimOnExceed: true,
		errorCodes: ['fastlane.template.quantity.validation'],
		errorParamX: 'value'
	},
	unit_coil: {
		type: 'string',
		row: 7,
		column: 9,
		mandatory: true,
		isDropDown: true,
		defaultValue: 'tons',
		errorCodes: ['fastlane.template.unitform.validation'],
		errorParamX: 'value',
		errorParamY: 'fastlane.itemlist.quality.calculator.coil'
	},
	slitting_coil: {
		type: 'string',
		row: 7,
		column: 9,
		mandatory: true,
		isDropDown: true,
		defaultValue: 'tons',
		errorCodes: ['fastlane.template.unitform.validation'],
		errorParamX: 'value',
		errorParamY: 'fastlane.itemlist.product.slitting_coil'
	},
	unit_sheet: {
		type: 'string',
		row: 7,
		column: 9,
		mandatory: true,
		isDropDown: true,
		defaultValue: 'sheets',
		errorCodes: ['fastlane.template.unitform.validation'],
		errorParamX: 'value',
		errorParamY: 'fastlane.itemlist.quality.calculator.sheet'
	},
	unit_blanks: {
		type: 'string',
		row: 7,
		column: 9,
		mandatory: true,
		isDropDown: true,
		defaultValue: 'blanks',
		errorCodes: ['fastlane.template.unitform.validation'],
		errorParamX: 'value',
		errorParamY: 'fastlane.itemlist.product.form_blank'
	},
	coating: {
		type: 'string',
		row: 7,
		column: 10,
		mandatory: true,
		maxLength: 50,
		trimOnExceed: true,
		defaultValue: '',
		errorCodes: ['fastlane.template.coating.validation'],
		errorParamX: 'size'
	},
	coilAxis: {
		type: 'string',
		row: 7,
		column: 11,
		mandatory: false,
		isDropDown: true,
		defaultValue: 'None',
		errorCodes: ['fastlane.template.coilaxis.validation'],
		errorParamX: 'value'
	},
	oiling: {
		type: 'string',
		row: 7,
		column: 12,
		mandatory: false,
		maxLength: 50,
		trimOnExceed: true,
		defaultValue: '',
		errorCodes: ['fastlane.template.oiling.validation'],
		errorParamX: 'size'
	},
	innerDiameter: {
		type: 'string',
		row: 7,
		column: 13,
		mandatory: false,
		isDropDown: true,
		defaultValue: 'None',
		errorCodes: ['fastlane.template.innerdiameter.validation'],
		errorParamX: 'value'
	},
	maxOutsideDiameter: {
		type: 'number',
		row: 7,
		column: 14,
		mandatory: false,
		min: 1,
		max: 999_999_999,
		decimalCount: 0,
		trimOnExceed: true,
		errorCodes: ['fastlane.template.outerdiameter.validation'],
		errorParamX: 'value'
	},
	additionalItemInfo: {
		type: 'string',
		row: 7,
		column: 15,
		mandatory: false,
		maxLength: 255,
		trimOnExceed: true,
		defaultValue: '',
		errorCodes: ['fastlane.template.addinfo.validation'],
		errorParamX: 'size'
	},
	rddDisplay: {
		type: 'date',
		row: 7,
		column: 17,
		mandatory: false,
		errorCodes: ['fastlane.template.rddweek.validation'],
		errorParamX: 'value',
		dateFormat: WEEKYEAR_FORMAT_PATTERN
	},
	rddDate: {
		type: 'date',
		row: 7,
		column: 18,
		mandatory: false,
		errorCodes: ['fastlane.template.rddday.validation'],
		errorParamX: 'value',
		dateFormat: DATE_FORMAT_PATTERN_DD_MM_YYYY
	},
	transportMode: {
		type: 'string',
		row: 7,
		column: 19,
		mandatory: true,
		isDropDown: true,
		defaultValue: 'None',
		errorCodes: ['fastlane.template.tmode.validation'],
		errorParamX: 'value'
	},
	incoterms: {
		type: 'string',
		row: 7,
		column: 20,
		mandatory: true,
		isDropDown: true,
		defaultValue: 'None',
		errorCodes: ['fastlane.template.incoterms.validation'],
		errorParamX: 'value'
	},
	packing: {
		type: 'string',
		row: 7,
		column: 21,
		mandatory: false,
		maxLength: 50,
		trimOnExceed: true,
		defaultValue: '',
		errorCodes: ['fastlane.template.packing.validation'],
		errorParamX: 'size'
	},
	plant: {
		type: 'string',
		row: 7,
		column: 22,
		mandatory: true,
		isDropDown: true,
		defaultValue: ' ',
		errorCodes: ['fastlane.template.mill.validation'],
		errorParamX: 'value'
	},
	shipToCustom: {
		type: 'string',
		row: 7,
		column: 23,
		mandatory: false,
		maxLength: 150,
		trimOnExceed: true,
		defaultValue: 'LATER',
		errorCodes: ['fastlane.template.deliveryaddress.validation'],
		errorParamX: 'size'
	},
};
